import React from "react";
import { isEmpty } from "lodash";
import { IconList } from "../IconList";
import { Typography } from "@/components/Typography";

import { Icons } from "@/types/icons";
import { PreviewEventDateBlockProps } from "./types";

import "./styles.scss";
import { FaRegCalendar } from "react-icons/fa";
import { ReactSVG } from "react-svg";
import { TimezonesService } from "@/services/TimezonesService";
import { makeUrls } from "@/services/AddToCalendarService";
import {
  getPreviewFormattedDateString,
  getPreviewFormattedTimeString,
} from "@/utils/dateTimeStringifyHelper";
import { Dropdown } from "../Dropdown";

export const PreviewEventDateBlock = ({
  data,
  title,
  labels,
  calendarEvent,
  themeColor,
}: PreviewEventDateBlockProps) => {
  if (!data || isEmpty(data)) return null;

  const { start, end, timezone, format, allDay } = data;

  const startContent = getPreviewFormattedDateString(start?.date);
  const endContent = getPreviewFormattedDateString(end?.date);
  const scheduleContent = allDay
    ? labels.allDay
    : getPreviewFormattedTimeString(start?.time, end?.time, format);

  const timezoneContent = TimezonesService.getGMTOffset(timezone);

  if (!startContent && !endContent && !scheduleContent && !data?.actionTitle) return null;

  document?.documentElement?.style.setProperty(
    "--themeButtonColor",
    themeColor?.buttonColor || "#1466b8"
  );
  document?.documentElement?.style.setProperty(
    "--themeBackgroundColor",
    themeColor?.backgroundColor || "#d1e5fa"
  );

  const URLS = makeUrls(calendarEvent);

  const rows = [
    {
      content: startContent,
      label: labels.start,
      render: !!startContent,
    },
    {
      content: endContent,
      label: labels.end,
      render: !!endContent,
    },
    {
      content: scheduleContent,
      label: labels.schedule,
      render: scheduleContent,
    },
    {
      content: timezoneContent,
      render: !allDay && timezoneContent,
      className: "event-date-block__row--align-right event-date-block__row--no-gap",
    },
  ];

  const addToCalendarOptions = [
    {
      dataQa: "add-to-calendar-option-ics",
      icon: "calendar-ics",
      label: "iCal",
      href: URLS?.ics,
    },
    {
      dataQa: "add-to-calendar-option-google",
      icon: "calendar-google",
      label: "Google",
      href: URLS?.google,
    },
    {
      dataQa: "add-to-calendar-option-outlook",
      icon: "calendar-outlook",
      label: "Outlook.com",
      href: URLS?.outlook,
    },
    {
      dataQa: "add-to-calendar-option-yahoo",
      icon: "calendar-yahoo",
      label: "Yahoo",
      href: URLS?.yahoo,
    },
  ];

  const renderRow = ({ className, content, render, label }, i) => {
    if (!render) return null;
    return (
      <div
        className={`event-date-block__row ${className || ""}`}
        key={`${label}-${i}`}>
        {label && (
          <Typography
            Tag="p"
            size="xs"
            classname="u-spacing-0 event-date-block__label">
            {label}
          </Typography>
        )}
        {content && (
          <Typography
            Tag="p"
            size="xs"
            classname="u-spacing-0">
            {content}
          </Typography>
        )}
      </div>
    );
  };

  return (
    <div className="u-card event-date-block">
      <IconList
        iconItem
        items={{
          icon: Icons.clock,
          title,
        }}
        tag={"h6"}
        semibold
      />
      {rows.map(renderRow)}
      {!!data?.actionTitle && (
        <div className="event-date-block__add-to-calendar">
          <Dropdown
            className="event-date-block__dropdown"
            dataQa="add-to-calendar">
            <Dropdown.Button dataQa="add-to-calendar-button">
              <div className="dropdown__button">
                <FaRegCalendar />
                {data?.actionTitle}
              </div>
            </Dropdown.Button>
            <Dropdown.Panel
              className="dropdown__panel"
              dataQa="add-to-calendar-panel">
              {addToCalendarOptions.map((option) => (
                <Dropdown.Option
                  as="a"
                  target="_blank"
                  key={option.dataQa}
                  className="dropdown__option"
                  dataQa="add-to-calendar-option"
                  href={option.href}>
                  <ReactSVG
                    className="dropdown__option-icon"
                    src={`/svg/${option.icon}.svg`}
                  />
                  {option.label}
                </Dropdown.Option>
              ))}
            </Dropdown.Panel>
          </Dropdown>
        </div>
      )}
    </div>
  );
};
